import { Button } from '@ctw/shared/components/button';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode, useId, useRef } from 'react';

export interface PatientSummarizationBannerProps {
  tone: 'caution' | 'error';
  icon: IconDefinition;
  title?: string;
  body: ReactNode;
  action?: ReactNode;
  dismissable?: boolean;
  isDismissed?: boolean;
  onDismiss?: () => void;
  variant?: 'inline' | 'standalone';
  background?: 'tone' | 'white';
  testId?: string;
}

export const PatientSummarizationBanner = ({
  tone,
  icon,
  title,
  body,
  action,
  dismissable = false,
  isDismissed = false,
  onDismiss,
  variant = 'inline',
  background = 'tone',
  testId,
}: PatientSummarizationBannerProps) => {
  const dismissButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  const handleDismiss = () => {
    // An ancestor becoming `inert` already moves focus off a focused descendant in engines that
    // implement that fix-up (e.g. Chromium). This `blur()` is belt-and-braces for one that
    // doesn't: it guarantees focus leaves this button — dropping to `document.body`, since the
    // component has no page context to hand it to something more specific — rather than relying
    // solely on `inert` taking effect.
    dismissButtonRef.current?.blur();
    onDismiss?.();
  };

  const isCaution = tone === 'caution';
  const isWhiteBg = background === 'white';

  let toneBg: string;
  if (isWhiteBg) {
    toneBg = 'bg-white';
  } else if (isCaution) {
    toneBg = 'bg-caution-background';
  } else {
    toneBg = 'bg-error-background';
  }

  const toneText = isCaution ? 'text-caution-text' : 'text-error-text';

  let toneBorder: string;
  if (isWhiteBg) {
    toneBorder = isCaution ? 'border-caution-badge' : 'border-error-badge';
  } else {
    toneBorder = isCaution ? 'border-caution-main/15' : 'border-error-main/15';
  }

  // `role="region"` plus the heading below make the banner a discoverable, named landmark, named
  // by the heading itself via `aria-labelledby` rather than a second copy of its text — one
  // source of truth that can't drift out of sync with what's rendered. ARIA requires a region to
  // be named, so without a title there's nothing to name it by and the landmark itself is dropped
  // rather than emitted unnamed — an unnamed region is worse than no region at all, since it
  // still surfaces in a screen reader's landmark list with no way to tell it apart from any
  // other. Spread rather than two literal `role`/`aria-labelledby` attributes so Biome's
  // `useAriaPropsSupportedByRole` (which can't resolve a conditional `role` value) doesn't flag
  // `aria-labelledby` as unsupported by a role it can't statically see is present.
  const regionProps = title ? { role: 'region' as const, 'aria-labelledby': titleId } : {};

  const content = (
    // This is not a live region: the banner (and its content) either doesn't exist yet or already
    // exists fully formed by the time it mounts, and a screen reader generally only announces a
    // live region's *change*, not content present at insertion — so an `aria-live` here would sit
    // on the element without ever firing.
    <div
      data-testid={testId}
      {...regionProps}
      className={twx('flex items-start justify-between gap-4 p-4', toneBg, toneText, {
        [`${toneBorder} border-t-2`]: variant === 'inline',
        [`my-5 rounded-lg border ${toneBorder}`]:
          variant === 'standalone' && background !== 'white',
        [`my-5 rounded-lg border-2 ${toneBorder}`]:
          variant === 'standalone' && background === 'white',
      })}
    >
      {variant === 'standalone' ? (
        <div>
          <div className={tw`flex items-center gap-3`}>
            <FontAwesomeIcon icon={icon} className={tw`relative top-3 h-5 w-5`} />
            {title && (
              <h2 id={titleId} className={tw`font-bold`}>
                {title}
              </h2>
            )}
          </div>
          <div className={twx('pl-8', { 'mt-0.5': Boolean(title) })}>{body}</div>
        </div>
      ) : (
        <div className={tw`flex items-start gap-2`}>
          <FontAwesomeIcon icon={icon} className={tw`h-4 w-4 pt-1`} />
          <div>
            {title && (
              <>
                <h2 id={titleId} className={tw`inline font-bold`}>
                  {title}
                </h2>{' '}
              </>
            )}
            {body}
          </div>
        </div>
      )}
      <div className={tw`flex items-center gap-2`}>
        {action}
        {dismissable && (
          <Button
            ref={dismissButtonRef}
            type="button"
            variant="circular"
            size="xs"
            ariaLabel="Dismiss"
            className={twx(
              'flex items-center justify-center bg-transparent p-1 shadow-none transition-all duration-300 hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15',
              toneText,
            )}
            onClick={handleDismiss}
          >
            <FontAwesomeIcon icon={faXmark} className={tw`h-4 w-4`} />
          </Button>
        )}
      </div>
    </div>
  );

  if (!dismissable) {
    return content;
  }

  return (
    <div
      inert={isDismissed}
      className={twx('grid transition-[grid-template-rows,opacity] duration-300 ease-out', {
        'grid-rows-[0fr] opacity-0': isDismissed,
        'grid-rows-[1fr] opacity-100': !isDismissed,
      })}
    >
      <div className={tw`min-h-0 overflow-hidden`}>{content}</div>
    </div>
  );
};
